<script lang="ts">
const __DsBlock = {
	type: 'input',
	category: 'input',
	hidden: true,
}
</script>
<script setup lang="ts">
import type { RelatedDisplayItem } from '#layers/blocks/components/Relation/RelatedItemListCard.vue';
import type { InputBlockProps } from '#layers/blocks/types/input-block';
import type { CompositePrimaryKey, GlobalSingleRelationFieldId } from '#layers/data-model/public';
import type { FormSaveContext, FormSaveError } from '#layers/forms/public';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useOverlay, useStore } from '#imports';
import { canAssignRole, connectMemberRoles, disconnectMemberRoles, getKeyId } from '#layers/access/public';
import { toStructuredError } from '#layers/base/public';
import { defineBlock } from '#layers/block-engine/public';
import RelatedItemListCard from '#layers/blocks/components/Relation/RelatedItemListCard.vue';
import { useCollectionByName } from '#layers/data-model/public';
import { injectFormBlockContext, useRelationFormField } from '#layers/forms/public';
import { LazyCollectionListingDrawer } from '#layers/listing/lazy';
import { buildAssignmentFilter } from './assignment-filter';
import { useAssignmentEntitlement } from './use-assignment-entitlement';
import { usePendingConnections } from './use-pending-connections';

interface MonospaceRole {
	id: string;
	name: string;
}

export interface InputRoleAssignmentBlockProps extends InputBlockProps {
	fieldId?: GlobalSingleRelationFieldId;
}

const props = defineProps<InputRoleAssignmentBlockProps>();

;

const store = useStore();
const { t } = useI18n();

const formBlockContext = injectFormBlockContext();
const { collection: roleCollection } = useCollectionByName('MonospaceRole');
const { referencedItemKey: userKey } = useRelationFormField(props);

const { allowedAssignableIds: allowedRoleIds, canAssign, canDisconnectItem: canDisconnectRole, allowedDisconnections } = useAssignmentEntitlement(canAssignRole());

const userId = computed(() => userKey.value?.id);

const { data: userRoles, refresh: refreshUserRoles } = store.MonospaceRole.query((q) => {
	// An unsaved member has no attached roles, and a filter on an undefined id is dropped
	// during serialization, which would match every role.
	if (!userId.value) return q.many({ enabled: false });

	return q.many({
		filter: {
			users: { _some: { userId: userId.value } },
		},
		// TODO(hannes) no-cache required here since we can't resolve relational filters locally
		fetchPolicy: 'no-cache',
	});
});

const currentRoleIds = computed(() => userRoles.value.map(({ id }) => id));

const {
	pendingConnections,
	pendingDisconnections,
	effectiveIds,
	hasChanges,
	connect,
	disconnect,
	undo,
	getItemState,
	clearConnections,
	clearDisconnections,
} = usePendingConnections(currentRoleIds);

// Load pending roles into the cache so the effective-id query can stay cache-only.
store.MonospaceRole.query((q) => pendingConnections.value.size === 0
	? q.many({ enabled: false })
	: q.many({ filter: { id: { _in: [...pendingConnections.value] } } }));

const { data: roleItems } = store.MonospaceRole.query((q) => q.many({ filter: { id: { _in: [...effectiveIds.value] } }, fetchPolicy: 'cache-only' }));

const displayItems = computed<RelatedDisplayItem<MonospaceRole>[]>(() =>
	roleItems.value.map((item) => ({
		id: item.id,
		data: item,
		state: getItemState(item.id),
	})),
);

async function handleSelectRoles() {
	if (!roleCollection.value) return;

	const modal = useOverlay().create(LazyCollectionListingDrawer, {
		props: {
			collectionId: roleCollection.value.id,
			selectionMode: 'multiple',
			filter: buildAssignmentFilter(allowedRoleIds.value, [...effectiveIds.value]),
		},
	});

	const result = await modal.open().result as { selection: CompositePrimaryKey[] } | undefined;

	if (result?.selection) {
		for (const key of result.selection) {
			connect(getKeyId(key)!);
		}
	}
}

function handleRemoveRole(item: MonospaceRole) {
	disconnect(item.id);
}

function handleUndoRole(item: MonospaceRole) {
	undo(item.id);
}

formBlockContext.onSave(async ({ itemKey }: FormSaveContext): Promise<FormSaveError[]> => {
	const errors: FormSaveError[] = [];
	const savedUserId = getKeyId({ id: itemKey.id })!;

	if (pendingConnections.value.size > 0) {
		try {
			await connectMemberRoles(savedUserId, [...pendingConnections.value]);
			clearConnections();
		}
		catch (error) {
			errors.push({ message: t('pages.settings.access.errors.attach_roles_failed'), source: toStructuredError(error) });
		}
	}

	// Re-check the allowlist at save time so a staged removal can never detach a role the user may not assign.
	const roleIdsToDisconnect = allowedDisconnections([...pendingDisconnections.value]);

	if (roleIdsToDisconnect.length > 0) {
		const junctionItems = store.MonospaceUserRole.peekMany({
			filter: { userId: savedUserId, roleId: { _in: roleIdsToDisconnect } },
		});

		try {
			await disconnectMemberRoles(savedUserId, roleIdsToDisconnect);

			for (const item of junctionItems) {
				store.MonospaceUserRole.clearItem(store.MonospaceUserRole.getKey(item)!);
			}

			clearDisconnections();
		}
		catch (error) {
			errors.push({ message: t('pages.settings.access.errors.detach_roles_failed'), source: toStructuredError(error) });
		}
	}

	try {
		await refreshUserRoles();
	}
	catch {
		// Refresh failure is non-fatal - user can manually refresh
	}

	return errors;
});

formBlockContext.reportHasChanges(() => hasChanges.value);
</script>

<template>
	<RelatedItemListCard
		v-if="roleCollection"
		:items="displayItems"
		:collection-id="roleCollection.id"
		:disabled
		:disabled-notice
		:show-edit="false"
		:show-create="false"
		:permissions="{ canConnect: canAssign, canDisconnectItem: canDisconnectRole }"
		show-add-existing
		@select="handleSelectRoles"
		@remove="handleRemoveRole"
		@undo="handleUndoRole"
	/>
</template>
